// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import AnunciosENoticias from "layouts/dashboard/components/AnunciosENoticias";
import B3SyncCard from "layouts/dashboard/components/B3SyncCard";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getRevenueExpensesSummary } from "layouts/receitaXdespesa/components/receitasxdespesas/revenueExpensesApi";
import { getRevenueExpenseCategorySummary } from "layouts/receitaXdespesa/components/receitasxdespesas/revenueExpensesApi";

import { getMonthlySummary } from "layouts/operations/api/operationsApi";
import { getWeekExpensesDetails } from "layouts/receitaXdespesa/components/receitasxdespesas/revenueExpensesApi";
import { AuthContext } from "context/authContext";

import { Steps, Hints } from "intro.js-react";
import "intro.js/introjs.css";
import { getProperty, getTotalPropertyValue } from "./api/dashboardApi";
import { useNavigate } from "react-router-dom";
import CustomItem from "layouts/posicao/posicaoChart";
import AssetTypeAllocationList from "layouts/posicao/assetTypeAllocationList";
import { buildAllocationByType, buildGlobalPosition } from "layouts/posicao/positionUtils";
import { useMaterialUIController } from "context";
import {
  buildPaymentRoute,
  clearCheckoutIntent,
  getPlanIntentContent,
  getStoredCheckoutPlanName,
  getStoredCheckoutPlanSlug,
} from "utils/checkoutFlow";
import { completeOnboarding, isOnboardingCompleted } from "utils/onboarding";
import ExpenseCategoryProgressCard from "layouts/dashboard/components/ExpenseCategoryProgressCard";
import SectionDescriptionHint from "layouts/dashboard/components/SectionDescriptionHint";
import ValueVisibilityToggleButton from "components/ValueVisibilityToggleButton";
import {
  HIDDEN_CURRENCY_LABEL,
  HIDDEN_VALUE_LABEL,
  getStoredValuesHidden,
  persistValuesHidden,
} from "utils/valueVisibility";
import {
  DASHBOARD_CARD_KEYS,
  getStoredDashboardCardVisibility,
  subscribeToDashboardCardVisibility,
} from "utils/dashboardCardVisibility";

const DASHBOARD_ONBOARDING_KEY = "patrimonio360onboarding-dashboard";

function MobileCollapsibleDashboardCard({
  title,
  description,
  mobileIcon,
  isMobile,
  isExpanded,
  onToggle,
  children,
}) {
  const shouldShowContent = !isMobile || isExpanded;

  return (
    <Card>
      <MDBox
        pt={2}
        px={2}
        pb={isMobile && !isExpanded ? 2 : 0}
        lineHeight={1.25}
        onClick={isMobile ? onToggle : undefined}
        sx={isMobile ? { cursor: "pointer", userSelect: "none" } : undefined}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <MDBox sx={{ flex: 1, minWidth: 0 }}>
            <MDBox display="flex" alignItems="center" gap={isMobile ? 1 : 1.25}>
              <MDBox
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "10px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: ({ palette: { info } }) => "rgba(26, 115, 232, 0.12)",
                  color: ({ palette: { info } }) => info.main,
                  flexShrink: 0,
                }}
              >
                <Icon fontSize="small">{mobileIcon}</Icon>
              </MDBox>
              <MDTypography 
              variant="h6" fontWeight="medium">
                {title}
              </MDTypography>
              <SectionDescriptionHint description={description} isMobile={isMobile} />
            </MDBox>
          </MDBox>
          {isMobile && (
            <Icon
              sx={{
                fontWeight: "bold",
                color: ({ palette: { info } }) => info.main,
                mt: 0.15,
              }}
            >
              {isExpanded ? "expand_less" : "expand_more"}
            </Icon>
          )}
        </MDBox>
      </MDBox>

      <Collapse in={shouldShowContent} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </Card>
  );
}

function Dashboard() {
  const [property, setProperty] = useState([]);
  const [isPropertyLoading, setIsPropertyLoading] = useState(true);
  const [revenueXExpenses, setRevenueXExpenses] = useState()
  const [saudeFinanceiraItems, setSaudeFinanceiraItems] = useState([])
  const [evolucaoPatrimonialData, setEvolucaoPatrimonialDataa] = useState({})
  const [evolucaoPatrimonialPercentageData, setEvolucaoPatrimonialPercentageDataa] = useState({})
  const [evolucaoPatrimonialPercentage, setEvolucaoPatrimonialPercentage] = useState(null)
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [screenWidth, setScreenWidth] = useState(null);
  const [totalPropertyValue, setTotalPropertyValue] = useState(0);
  const [pendingPlanSlug, setPendingPlanSlug] = useState(() => getStoredCheckoutPlanSlug());
  const [pendingPlanName, setPendingPlanName] = useState(() => getStoredCheckoutPlanName());
  const [areDashboardValuesHidden, setAreDashboardValuesHidden] = useState(getStoredValuesHidden);
  const [dashboardCardVisibility, setDashboardCardVisibility] = useState(
    getStoredDashboardCardVisibility
  );
  const [mobileSectionsExpanded, setMobileSectionsExpanded] = useState({
    saudeFinanceira: false,
    posicaoGlobal: false,
    exposicaoTipoAtivo: false,
    exposicaoAtualAtivo: false,
  });
  const [controller] = useMaterialUIController();
  const { darkMode, userTier, isDarkMode } = controller;
  const navigate = useNavigate();
  const isMobileSectionLayout = useMediaQuery("(max-width:900px)", { noSsr: true });

  useEffect(() => {
    //TODO: Ajustar essas chamadas para usar o customFetch;
    getPropertyPosition();
    getRevenueExpensesSummaryMethod();
    getSaudeFinanceiraChartData();
    getMonthlySummaryData();
    getTotalPropertyValuex();

    setScreenWidth(getWindowDimensions().width)

    setStepsEnabled(!isOnboardingCompleted(DASHBOARD_ONBOARDING_KEY));
  }, []);

  useEffect(() => {
    persistValuesHidden(areDashboardValuesHidden);
  }, [areDashboardValuesHidden]);

  useEffect(
    () => subscribeToDashboardCardVisibility(setDashboardCardVisibility),
    []
  );

  const isDashboardCardVisible = useCallback(
    (cardKey) => dashboardCardVisibility[cardKey] !== false,
    [dashboardCardVisibility]
  );

  const getTotalPropertyValuex = async () => {
    await getTotalPropertyValue().then((data) => {
      setTotalPropertyValue(data);
    });
  }


  function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height
    };
  }

  useEffect(() => {
    getVariationCurrentMonthRevenueAndLastMonthRevenue();
  }, [evolucaoPatrimonialData])

  const pendingPlanIntent = getPlanIntentContent(pendingPlanSlug);

  useEffect(() => {
    if (!pendingPlanIntent) {
      return;
    }

    if (!pendingPlanIntent.checkoutEnabled || Number(userTier ?? 1) >= pendingPlanIntent.tierId) {
      clearCheckoutIntent({ preserveEmail: true });
      setPendingPlanSlug(null);
      setPendingPlanName(null);
    }
  }, [pendingPlanIntent, userTier]);

  const getContabilDate = () => {
    const dataAtual = new Date();

    const meses = [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    const mesPorExtenso = meses[dataAtual.getMonth()].substring(0, 3);
    const ano = dataAtual.getFullYear();

    return `${mesPorExtenso}/${ano}`;
  }

  const getPropertyPosition = async () => {
    setIsPropertyLoading(true);

    await getProperty()
      .then((data) => {
        setProperty(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setProperty([]);
      })
      .finally(() => {
        setIsPropertyLoading(false);
      });
  };

  const getRevenueExpensesSummaryMethod = async () => {
    await getRevenueExpensesSummary().then((data) => {
      setRevenueXExpenses(data);
    })
  }

  const getSaudeFinanceiraChartData = async () => {
    await getRevenueExpenseCategorySummary()
      .then((data) => {
        setSaudeFinanceiraItems(
          (Array.isArray(data) ? data : []).map((item) => ({
            category: item.category,
            value: Number(item.value ?? 0),
            configuredLimitValue: item.configuredLimitValue != null ? Number(item.configuredLimitValue) : null,
            hasConfiguredLimit: Boolean(item.hasConfiguredLimit),
          }))
        );
      })
  }

  const getMonthlySummaryData = async () => {
    await getMonthlySummary()
      .then((data) => {
        const monthlySummary = (Array.isArray(data) ? data : []).slice(-12);
        const labels = monthlySummary.map((item) => item.month.substring(0, 3));

        setEvolucaoPatrimonialDataa({
          labels,
          datasets: { label: 'R$', data: monthlySummary.map((item) => item.value) }
        });

        setEvolucaoPatrimonialPercentageDataa({
          labels,
          datasets: { label: '%', data: monthlySummary.map((item) => item.growthPercentaCompatingLastMonth) }
        });
      });
  }

  const getVariationCurrentMonthRevenueAndLastMonthRevenue = () => {

    let lastValue = evolucaoPatrimonialData?.datasets?.data[evolucaoPatrimonialData?.datasets?.data.length - 1];
    let initialValue = evolucaoPatrimonialData?.datasets?.data[evolucaoPatrimonialData?.datasets?.data.length - 2];

    if (typeof (lastValue) == 'number' && typeof (initialValue) == 'number') {
      let result = 100 - ((lastValue * 100) / initialValue);
      setEvolucaoPatrimonialPercentage(Number(result * -1).toFixed(2));
    }
  }

  const formatPercentage = (value) => Number(value ?? 0).toFixed(2);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  const displayCurrency = (value) =>
    areDashboardValuesHidden ? HIDDEN_CURRENCY_LABEL : formatCurrency(value);

  const formatChartPercentage = useCallback((value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "";
    }

    return `${numericValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }, []);

  const formatSignedChartPercentage = useCallback((value) => {
    const numericValue = Number(value);
    const formattedValue = formatChartPercentage(numericValue);

    if (!formattedValue) {
      return "";
    }

    return numericValue > 0 ? `+${formattedValue}` : formattedValue;
  }, [formatChartPercentage]);

  const monthlyPercentagePointLabels = useMemo(
    () => (evolucaoPatrimonialPercentageData?.datasets?.data || []).map(formatSignedChartPercentage),
    [evolucaoPatrimonialPercentageData, formatSignedChartPercentage]
  );

  const formatChartCurrency = useCallback((value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "";
    }

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    }).format(numericValue);
  }, []);

  const formatCompactChartCurrency = useCallback((value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "";
    }

    const absoluteValue = Math.abs(numericValue);

    if (absoluteValue >= 1000000) {
      return `R$ ${(numericValue / 1000000).toLocaleString("pt-BR", {
        maximumFractionDigits: 1,
      })} mi`;
    }

    if (absoluteValue >= 1000) {
      return `R$ ${(numericValue / 1000).toLocaleString("pt-BR", {
        maximumFractionDigits: 0,
      })} mil`;
    }

    return formatChartCurrency(numericValue);
  }, [formatChartCurrency]);

  const monthlyValuePointLabels = useMemo(
    () => (evolucaoPatrimonialData?.datasets?.data || []).map(formatCompactChartCurrency),
    [evolucaoPatrimonialData, formatCompactChartCurrency]
  );

  const chartPercentagePointLabels = useMemo(
    () => (isMobileSectionLayout ? [] : monthlyPercentagePointLabels),
    [isMobileSectionLayout, monthlyPercentagePointLabels]
  );
  const chartValuePointLabels = useMemo(
    () => (isMobileSectionLayout ? [] : monthlyValuePointLabels),
    [isMobileSectionLayout, monthlyValuePointLabels]
  );

  const toggleDashboardValuesVisibility = () => {
    setAreDashboardValuesHidden((currentValue) => !currentValue);
  };

  const onExit = () => {
    completeOnboarding(DASHBOARD_ONBOARDING_KEY);
    setStepsEnabled(false);
  };

  const { user, isLoggedIn } = useContext(AuthContext);
  const shouldShowDeferredUpgrade = Boolean(
    isLoggedIn &&
    pendingPlanIntent?.checkoutEnabled &&
    Number(userTier ?? 1) < pendingPlanIntent.tierId
  );
  const selectedPlanName = pendingPlanName || pendingPlanIntent?.name;
  const deferredUpgradeHighlightColumns = pendingPlanIntent?.dashboardHighlights?.length === 4 ? 6 : 4;
  const showTotalWealthCard = isDashboardCardVisible(DASHBOARD_CARD_KEYS.totalWealth);
  const showMonthlyRevenueCard = isDashboardCardVisible(DASHBOARD_CARD_KEYS.monthlyRevenue);
  const showMonthlyExpensesCard = isDashboardCardVisible(DASHBOARD_CARD_KEYS.monthlyExpenses);
  const showAvailableBalanceCard = isDashboardCardVisible(DASHBOARD_CARD_KEYS.availableBalance);
  const showPatrimonyEvolutionPercentageCard = isDashboardCardVisible(
    DASHBOARD_CARD_KEYS.patrimonyEvolutionPercentage
  );
  const showPatrimonyEvolutionCard = isDashboardCardVisible(
    DASHBOARD_CARD_KEYS.patrimonyEvolution
  );
  const showMonthlyExpensesByCategoryCard = isDashboardCardVisible(
    DASHBOARD_CARD_KEYS.monthlyExpensesByCategory
  );
  const showGlobalPositionCard = isDashboardCardVisible(DASHBOARD_CARD_KEYS.globalPosition);
  const showAssetTypeExposureCard = isDashboardCardVisible(DASHBOARD_CARD_KEYS.assetTypeExposure);
  const showCurrentAssetExposureCard = isDashboardCardVisible(
    DASHBOARD_CARD_KEYS.currentAssetExposure
  );
  const hasSummaryCards =
    showTotalWealthCard ||
    showMonthlyRevenueCard ||
    showMonthlyExpensesCard ||
    showAvailableBalanceCard;
  const hasEvolutionCards =
    showPatrimonyEvolutionPercentageCard ||
    showPatrimonyEvolutionCard ||
    showMonthlyExpensesByCategoryCard;
  const hasPortfolioCards =
    showGlobalPositionCard || showAssetTypeExposureCard || showCurrentAssetExposureCard;

  const dismissPendingUpgrade = () => {
    clearCheckoutIntent({ preserveEmail: true });
    setPendingPlanSlug(null);
    setPendingPlanName(null);
  };

  const toggleMobileSection = (sectionKey) => {
    setMobileSectionsExpanded((currentValue) => ({
      ...currentValue,
      [sectionKey]: !currentValue[sectionKey],
    }));
  };

  const stepsDesktop = [
    {
      element: '.menuHamburger',
      intro: 'Clique aqui para abrir ou fechar o menu lateral e acessar todas as funcionalidades.',
    },
    {
      element: '.menu-dashboard',
      intro: 'No menu "Dashboard", você acompanha uma visão geral do seu patrimônio.',
    },
    {
      element: '.patrimonioTotal',
      intro: 'Este card mostra o valor total do seu patrimônio.',
      position: 'right',
      tooltipClass: 'myTooltipClass',
      dashboardCardKey: DASHBOARD_CARD_KEYS.totalWealth,
    },
    {
      element: '.receita',
      intro: 'Este card mostra o valor total das suas receitas no mês atual.',
      dashboardCardKey: DASHBOARD_CARD_KEYS.monthlyRevenue,
    },
    {
      element: '.despesa',
      intro: 'Este card mostra o valor total das suas despesas no mês atual.',
      dashboardCardKey: DASHBOARD_CARD_KEYS.monthlyExpenses,
    },
    {
      element: '.saldoTotal',
      intro: 'Este card mostra o saldo disponível no mês, calculado pela diferença entre receitas e despesas.',
      dashboardCardKey: DASHBOARD_CARD_KEYS.availableBalance,
    },
    {
      element: '.evolucaoPatrimonialPercentual',
      intro: 'Aqui é possível acompanhar, de forma resumida, o percentual de ganho ou perda do seu patrimônio nos últimos 12 meses.',
      dashboardCardKey: DASHBOARD_CARD_KEYS.patrimonyEvolutionPercentage,
    },
    {
      element: '.evolucaoPatrimonial',
      intro: 'Patrimônio acumulado por mês',
      dashboardCardKey: DASHBOARD_CARD_KEYS.patrimonyEvolution,
    },
    {
      element: '.saudeFinanceira',
      intro: 'Representa o balanço mensal, ou seja, a diferença entre receitas e despesas.',
      dashboardCardKey: DASHBOARD_CARD_KEYS.monthlyExpensesByCategory,
    },
    {
      element: '.tabelaAtivos',
      intro: 'Esta seção reúne os ativos cadastrados no menu "Operações".',
      dashboardCardKey: DASHBOARD_CARD_KEYS.currentAssetExposure,
    },
  ];

  const visibleStepsDesktop = stepsDesktop
    .filter((step) => !step.dashboardCardKey || isDashboardCardVisible(step.dashboardCardKey))
    .map(({ dashboardCardKey, ...step }) => step);

  const handleBeforeChange = (nextStepIndex) => {
    if (screenWidth < 1200 && nextStepIndex == 0) {
      setTimeout(() => {
        document.getElementsByClassName("menuHamburger")[0].click()

      }, 400);
    }

    if (screenWidth < 1200 && nextStepIndex == 2) {
      document.getElementsByClassName("menuHamburger")[0].click()
    }
  };

  function getValidPercentuais(dados) {
    return (dados?.datasets?.data || []).filter(
      (value) => typeof value === 'number' && Number.isFinite(value)
    );
  }

  function calcularPercentualComposto(percentuais) {
    if (percentuais.length === 0) return 0;

    const fatorComposto = percentuais.reduce((accumulator, percentual) => {
      return accumulator * (1 + percentual / 100);
    }, 1);

    return (fatorComposto - 1) * 100;
  }

  function removerOutliersPercentuais(dados) {
    const percentuais = getValidPercentuais(dados);

    if (percentuais.length < 4) return percentuais;

    const sorted = [...percentuais].sort((a, b) => a - b);
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const iqr = q3 - q1;
    const limiteInferior = q1 - 1.5 * iqr;
    const limiteSuperior = q3 + 1.5 * iqr;

    return percentuais.filter(
      (percentual) => percentual >= limiteInferior && percentual <= limiteSuperior
    );
  }

  function getEvolucaoPatrimonialPercentual12Meses(dados) {
    return calcularPercentualComposto(getValidPercentuais(dados));
  }

  function getEvolucaoPatrimonialPercentualSemOutliers(dados) {
    return calcularPercentualComposto(removerOutliersPercentuais(dados));
  }

  const globalPosition = buildGlobalPosition(property);
  const allocationByType = buildAllocationByType(property);

  return (
    <DashboardLayout>
      <Steps
        enabled={stepsEnabled}
        steps={visibleStepsDesktop}
        initialStep={screenWidth < 1200 ? 0 : Math.min(1, visibleStepsDesktop.length - 1)}
        onExit={onExit}
        onBeforeChange={handleBeforeChange}
        onBeforeExit={false}
        exitOnOverlayClick={false}
        onComplete={onExit}
        options={{
          doneLabel: 'Entendi!',
          prevLabel: '<',
          nextLabel: '>'
        }}
      />
      <DashboardNavbar
        actions={
          <ValueVisibilityToggleButton
            valuesHidden={areDashboardValuesHidden}
            onToggle={toggleDashboardValuesVisibility}
            scopeLabel="do dashboard"
          />
        }
      />
      <MDBox py={3}   >
        {shouldShowDeferredUpgrade && (
          <MDBox mb={10} >
            <Card col>
              <MDBox 
                p={3}
              >
                <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" gap={2} >
                  <MDBox maxWidth="42rem" >
                    <MDTypography
                      variant="overline"
                      sx={{ color: "#0f766e", fontWeight: 700, letterSpacing: "0.08em" }}
                    >
                      Plano selecionado: {selectedPlanName}
                    </MDTypography>
                    <MDTypography variant="h5" mt={0.5}>
                      {pendingPlanIntent.dashboardTitle}
                    </MDTypography>
                    <MDTypography variant="button" color="text" display="block" mt={1.25}>
                      {pendingPlanIntent.dashboardDescription}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" flexWrap="wrap" gap={1.5} alignSelf="flex-start">
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() => navigate(buildPaymentRoute(pendingPlanIntent.slug))}
                    >
                      Ativar {selectedPlanName}
                    </MDButton>
                    <MDButton variant="outlined" color="info" onClick={dismissPendingUpgrade}>
                      Continuar sem pagar por enquanto
                    </MDButton>
                  </MDBox>
                </MDBox>

              
              </MDBox>
            </Card>
          </MDBox>
          // </ThemeProvider>
        )}

        {hasSummaryCards && (
          <Grid container spacing={3}>
          {/* B3 sync desligado */}
          {1==2 && (
            <Grid item xs={12}>
              <B3SyncCard />
            </Grid>
            )}
            {showTotalWealthCard && (
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5} className="patrimonioTotal">
                  <ComplexStatisticsCard
                    color="dark"
                    icon="account_balance_wallet"
                    title="Patrimônio total"
                    count={displayCurrency(totalPropertyValue)}
                    toolTipMessage="Soma do valor atual de todos os ativos cadastrados."
                  />
                </MDBox>
              </Grid>
            )}
            {showMonthlyRevenueCard && (
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5} className="receita">
                  <ComplexStatisticsCard
                    icon="leaderboard"
                    title={`Receita ${getContabilDate()}`}
                    count={displayCurrency(revenueXExpenses?.revenue ?? '0')}
                    toolTipMessage="Total de receitas registradas no mês atual."
                  // percentage={{
                  //   color: "success",
                  //   amount: "+3%",
                  //   label: "than last month",
                  // }}
                  />
                </MDBox>
              </Grid>
            )}
            {showMonthlyExpensesCard && (
              <Grid item xs={12} md={6} lg={3}>

                <MDBox mb={1.5} className="despesa">
                  <ComplexStatisticsCard
                    color="success"
                    icon="store"
                    title={`Despesas ${getContabilDate()}`}
                    count={displayCurrency(revenueXExpenses?.expenses ?? '0')}
                    toolTipMessage="Total de despesas registradas no mês atual."
                  // percentage={{
                  //   color: "success",
                  //   amount: "+1%",
                  //   label: "than yesterday",
                  // }}
                  />
                </MDBox>
              </Grid>
            )}
            {showAvailableBalanceCard && (
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5} className="saldoTotal">
                  <ComplexStatisticsCard
                    color="primary"
                    icon="person_add"
                    title="Saldo disponível"
                    count={displayCurrency(revenueXExpenses?.remaningValue ?? '0')}
                    toolTipMessage="Saldo do mês, calculado pela diferença entre receitas e despesas."
                  // percentage={{
                  //   color: "success",
                  //   amount: "9",
                  //   label: "Just updated",
                  // }}
                  />
                </MDBox>
              </Grid>
            )}
          </Grid>
        )}
        {hasEvolutionCards && (
          <MDBox mt={hasSummaryCards ? 4.5 : 0}>
            <Grid container spacing={3}>
              {showPatrimonyEvolutionPercentageCard && (
                <Grid item xs={12} md={6} lg={6} >
                  <MDBox mb={3} className="evolucaoPatrimonialPercentual">
                    <ReportsLineChart
                      color="info"
                      title="Evolução patrimonial percentual"
                      description={
                        <>
                          <strong>
                            {formatPercentage(
                              getEvolucaoPatrimonialPercentual12Meses(evolucaoPatrimonialPercentageData)
                            )}%
                          </strong>
                          , nos últimos 12 meses
                          <span>
                            <br />
                            <strong>{formatPercentage(getEvolucaoPatrimonialPercentualSemOutliers(evolucaoPatrimonialPercentageData))}%</strong>, desconsiderando os valores atípicos
                          </span>
                        </>
                      }
                      date="Atualizado há 5 minutos"
                      chart={evolucaoPatrimonialPercentageData}
                      valueFormatter={formatChartPercentage}
                      pointLabels={chartPercentagePointLabels}
                    />
                  </MDBox>
                </Grid>
              )}
              {showPatrimonyEvolutionCard && (
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox mb={3} className="evolucaoPatrimonial" sx={{height: '94%'}}>
                    <ReportsLineChart
                      color="success"
                      title="Evolução patrimonial"
                      description={
                        areDashboardValuesHidden ? (
                          HIDDEN_VALUE_LABEL
                        ) : (
                          <>
                            {evolucaoPatrimonialPercentage &&
                            <>
                              <strong>({evolucaoPatrimonialPercentage > 0 && '+'}{evolucaoPatrimonialPercentage}%)</strong> comparado ao mês anterior.
                            </>}
                          </>
                        )
                      }
                      date="Atualizado há 5 min"
                      chart={evolucaoPatrimonialData}
                      valuesHidden={areDashboardValuesHidden}
                      valueFormatter={formatChartCurrency}
                      pointLabels={chartValuePointLabels}
                    />
                  </MDBox>
                </Grid>
              )}
              {showMonthlyExpensesByCategoryCard && (
                <Grid item xs={12} md={12} lg={12}>
                  <MDBox mb={3} className="saudeFinanceira" sx={{ height: isMobileSectionLayout ? "auto" : "94%" }}>
                    <ExpenseCategoryProgressCard
                      title="Despesas do mês"
                      description="Despesas do mês atual agrupadas por categoria, comparadas com o limite mensal configurado para cada uma."
                      date="Atualizado no mês atual"
                      items={saudeFinanceiraItems}
                      darkMode={darkMode}
                      mobileIcon="health_and_safety"
                      toolTipMessage="Cada linha mostra o total gasto por categoria no mês atual e compara esse valor com o limite configurado pelo usuário."
                      mobileCollapseEnabled={isMobileSectionLayout}
                      mobileExpanded={mobileSectionsExpanded.saudeFinanceira}
                      onToggleMobileCollapse={() => toggleMobileSection("saudeFinanceira")}
                      valuesHidden={areDashboardValuesHidden}
                      // sx={{marginBotton: 10}}
                    />
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        )}
        {hasPortfolioCards && (
          <MDBox>
            <Grid container spacing={3}>
              {showGlobalPositionCard && (
                <Grid item xs={12} md={12} lg={6}>
                  <MobileCollapsibleDashboardCard
                    title="Posição Global"
                    description="Gráfico da posição patrimonial global"
                    mobileIcon="pie_chart"
                    isMobile={isMobileSectionLayout}
                    isExpanded={mobileSectionsExpanded.posicaoGlobal}
                    onToggle={() => toggleMobileSection("posicaoGlobal")}
                  >
                    {globalPosition.length > 0 ? (
                      <CustomItem
                        data={globalPosition}
                        isDarkMode={darkMode}
                        valuesHidden={areDashboardValuesHidden}
                      />
                    ) : (
                      <MDBox px={2} pb={3}>
                        <MDTypography variant="button" color="text">
                          {isPropertyLoading ? "Carregando posição..." : "Nenhuma posição encontrada para exibir o gráfico."}
                        </MDTypography>
                      </MDBox>
                    )}
                  </MobileCollapsibleDashboardCard>
                </Grid>
              )}
              {showAssetTypeExposureCard && (
                <Grid item xs={12} md={12} lg={6}>
                  <MobileCollapsibleDashboardCard
                    title="Exposição por tipo de ativo"
                    description="Distribuição percentual da exposição da carteira por tipo de ativo"
                    mobileIcon="layers"
                    isMobile={isMobileSectionLayout}
                    isExpanded={mobileSectionsExpanded.exposicaoTipoAtivo}
                    onToggle={() => toggleMobileSection("exposicaoTipoAtivo")}
                  >
                    <AssetTypeAllocationList
                      data={allocationByType}
                      isLoading={isPropertyLoading}
                      valuesHidden={areDashboardValuesHidden}
                    />
                  </MobileCollapsibleDashboardCard>
                </Grid>
              )}
              {showCurrentAssetExposureCard && (
                <Grid item xs={12} md={12} lg={12}>
                  <Projects
                    mobileCollapseEnabled={isMobileSectionLayout}
                    mobileExpanded={mobileSectionsExpanded.exposicaoAtualAtivo}
                    onToggleMobileCollapse={() => toggleMobileSection("exposicaoAtualAtivo")}
                    valuesHidden={areDashboardValuesHidden}
                  />
                </Grid>
              )}
              {/* <Grid item xs={12} md={6} lg={4}>
                <AnunciosENoticias />
              </Grid> */}
            </Grid>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
